/**
 * Canonical Transform - Bulletproof Fix
 * Handles all server envelope variants and ensures identifiers/charges are extracted
 */

import { CAT, type CatKey } from '@/shared/CAT';

type AnyRec = Record<string, any>;

function safeJsonParse<T=any>(x: any): T | null {
  try { return typeof x === 'string' ? JSON.parse(x) : null; } catch { return null; }
}

function findFirst<T=any>(obj: AnyRec, paths: string[]): T | undefined {
  for (const p of paths) {
    const parts = p.split('.').filter(Boolean);
    let cur: any = obj;
    let ok = true;
    for (const part of parts) {
      if (cur && typeof cur === 'object' && part in cur) cur = cur[part];
      else { ok = false; break; }
    }
    if (ok && cur !== undefined) return cur as T;
  }
  return undefined;
}

/**
 * Unwrap all known server envelopes and normalize keys we care about.
 * Returns:
 *  - resultsObj: the top-level bill fields object (supplierName, invoiceDate, …)
 *  - rawPayload: nested "raw_payload" (object), if present
 *  - identifiers: the array of identifiers (never undefined, so UI is stable)
 */
export function unwrapApiPayload(apiResponse: AnyRec) {
  // Common top-level wrappers
  const body =
    apiResponse?.results ??
    apiResponse?.data ??
    apiResponse?.Data ??
    apiResponse;

  // Some servers wrap useful object as JSON string under "Data" or "data"
  const jsonInData = safeJsonParse<AnyRec>(apiResponse?.Data) || safeJsonParse<AnyRec>(apiResponse?.data);
  const body2 = jsonInData ?? body;

  // Results can be under "results" inside that JSON too
  const resultsObj: AnyRec =
    body2?.results ?? body2;

  // Raw payload may exist under different casings
  let rawPayload: AnyRec | undefined =
    resultsObj?.raw_payload ??
    resultsObj?.rawPayload ??
    apiResponse?.raw_payload ??
    apiResponse?.rawPayload;

  // If rawPayload is a JSON string, parse it
  rawPayload = rawPayload && typeof rawPayload === 'string' ? safeJsonParse(rawPayload) || rawPayload : rawPayload;

  // Try to locate identifiers array across variants
  // paths are checked in order until the first hit
  let identifiers: any[] =
    findFirst<any[]>(resultsObj, [
      'raw_payload.parseridentifiersdata', // snake under raw_payload
      'rawPayload.parseridentifiersdata',
      'parseridentifiersdata',            // snake at results root
      'raw_payload.parserIdentifiersData', // camel variants
      'rawPayload.parserIdentifiersData',
      'parserIdentifiersData',
    ]) ??
    findFirst<any[]>(apiResponse, [
      'raw_payload.parseridentifiersdata',
      'rawPayload.parseridentifiersdata',
      'parseridentifiersdata',
      'raw_payload.parserIdentifiersData',
      'rawPayload.parserIdentifiersData',
      'parserIdentifiersData',
    ]) ??
    [];

  // If someone returned it as a JSON string
  if (typeof identifiers === 'string') {
    identifiers = safeJsonParse<any[]>(identifiers) ?? [];
  }

  // Strong guarantee: arrays only
  if (!Array.isArray(identifiers)) identifiers = [];

  return { resultsObj, rawPayload, identifiers };
}

const BASIC_KEYS = new Set([
  'invoice_number','invoice_date','billing_period_from','billing_period_to',
  'supplier','account_number','parsed_at','filename','fileextension','templatename','templateversion'
]);
const FINANCIAL_KEYS = /(amount|balance|payment|fee|tax|charge|discount|total|due)/i;
const ADDRESS_KEYS   = /^(service_address|address|city|state|postcode|post_code|country)$/i;
const USAGE_KEYS     = /^(usage|consumption|rate)$/i;

const isRenderable = (v: unknown) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';   // ignore empty string only
  if (Array.isArray(v)) return false;                  // we iterate arrays explicitly
  if (typeof v === 'object') return false;             // we flatten only whitelisted objects below
  return true; // keep 0, "0", negatives, booleans
};

export interface CanonicalField {
  label: string;
  value: any;
  keyPath: string;
}

export interface IdentifierRow {
  identifier: string;
  utilityType: string;
  siteAddress: string;
  siteCity: string;
  siteState: string;
  siteCountry: string;
  sitePostCode: string;
  periodFrom: string;
  periodTo: string;
  serviceTotal: number | null;
}

export interface MeterRow {
  identifier: string;
  meterNumber: string;
  multiplier: string;
  previousReading: string;
  currentReading: string;
  quantity: string;
  unit: string;
}

export interface LineItemRow {
  identifier: string;
  meterNumber: string;
  description: string;
  quantity: string;
  unit: string;
  rate: string;
  total: string;
}

export interface CanonicalResult {
  categories: Record<string, CanonicalField[]>;
  identifiers: IdentifierRow[];
  meters: MeterRow[];
  lineItems: LineItemRow[];
  totalFields: number;
}

function put(
  categories: Record<string, CanonicalField[]>,
  catKey: CatKey,
  label: string,
  value: any,
  keyPath: string,
  takenKeys: Set<string>
) {
  if (!isRenderable(value) || takenKeys.has(keyPath)) return;
  const bucket = CAT[catKey];
  (categories[bucket] ??= []).push({ label, value, keyPath });
  takenKeys.add(keyPath);
}

export function canonicalTransform(apiResponse: any): CanonicalResult {
  // 1) Unwrap
  const { resultsObj, rawPayload, identifiers } = unwrapApiPayload(apiResponse);
  console.debug('[canonicalTransform] keys', Object.keys(resultsObj || {}));
  console.debug('[canonicalTransform] identifiers.count', identifiers.length);

  // 2) Build categories skeleton using CAT constants
  const categories: Record<string, CanonicalField[]> = {
    [CAT.Basic]: [], [CAT.Financial]: [], [CAT.Address]: [], [CAT.Usage]: [],
    [CAT.Meter]: [], [CAT.Identifier]: [], [CAT.LineItems]: [],
    [CAT.Technical]: [], [CAT.Other]: [],
  };

  const takenKeys = new Set<string>();

  // 3) Route TOP-LEVEL scalar fields only (do NOT touch arrays/objects here)
  Object.entries(resultsObj || {}).forEach(([k, v]) => {
    if (v === null || v === undefined) return;
    if (Array.isArray(v) || typeof v === 'object') return;   // <-- keep arrays out
    const kl = k.toLowerCase();
    if (BASIC_KEYS.has(kl))      return put(categories, 'Basic', k, v, `top.${k}`, takenKeys);
    if (FINANCIAL_KEYS.test(kl)) return put(categories, 'Financial', k, v, `top.${k}`, takenKeys);
    if (ADDRESS_KEYS.test(kl))   return put(categories, 'Address', k, v, `top.${k}`, takenKeys);
    if (USAGE_KEYS.test(kl))     return put(categories, 'Usage', k, v, `top.${k}`, takenKeys);
    return put(categories, 'Other', k, v, `top.${k}`, takenKeys);
  });

  // ---------- USAGE (top-level) ----------
  const topUsageCandidates: Array<[string, any]> = [
    ['Annual Usage', resultsObj?.annualUsage ?? resultsObj?.annual_usage],
    ['Usage',        resultsObj?.usage],
    ['Consumption',  resultsObj?.consumption],
    ['Rate',         resultsObj?.rate],
  ];

  topUsageCandidates.forEach(([label, val]) => {
    if (val !== null && val !== undefined && val !== '') {
      put(categories, 'Usage', label, val, `top.usage.${label}`, takenKeys);
    }
  });

  // 4) IDENTIFIERS: always iterate explicitly, never through a generic router
  const identifierRows: IdentifierRow[] = [];
  const meterRows: MeterRow[] = [];
  const lineItemRows: LineItemRow[] = [];

  identifiers.forEach((id, idx) => {
    if (!id || typeof id !== 'object') return;

    // Identifier facts (to Identifier category)
    put(categories, 'Identifier', 'Identifier', id.identifier, `ident.${idx}.identifier`, takenKeys);
    put(categories, 'Identifier', 'Utility Type', id.utilityType, `ident.${idx}.utilityType`, takenKeys);
    put(categories, 'Identifier', 'Service Total', id.serviceTotal, `ident.${idx}.serviceTotal`, takenKeys);
    put(categories, 'Identifier', 'Site Address', id.siteAddress, `ident.${idx}.siteAddress`, takenKeys);
    put(categories, 'Identifier', 'Site City', id.siteCity, `ident.${idx}.siteCity`, takenKeys);
    put(categories, 'Identifier', 'Site State', id.siteState, `ident.${idx}.siteState`, takenKeys);
    put(categories, 'Identifier', 'Site Country', id.siteCountry, `ident.${idx}.siteCountry`, takenKeys);
    put(categories, 'Identifier', 'Site Post Code', id.sitePostCode, `ident.${idx}.sitePostCode`, takenKeys);
    put(categories, 'Identifier', 'Period From', id.periodFrom, `ident.${idx}.periodFrom`, takenKeys);
    put(categories, 'Identifier', 'Period To', id.periodTo, `ident.${idx}.periodTo`, takenKeys);
    put(categories, 'Identifier', 'Read Type', id.readType, `ident.${idx}.readType`, takenKeys);
    put(categories, 'Identifier', 'Usage Month', id.usageMonth, `ident.${idx}.usageMonth`, takenKeys);

    // Usage fields inside identifier (period usage per service)
    const idUsageCandidates: Array<[string, any]> = [
      ['Service Usage', id.serviceUsage],
      ['Usage Month',   id.usageMonth],
      ['Period From',   id.periodFrom],
      ['Period To',     id.periodTo],
      ['Read Type',     id.readType],
    ];
    idUsageCandidates.forEach(([label, val]) => {
      if (val !== null && val !== undefined && val !== '') {
        put(categories, 'Usage', label, val, `ident.${idx}.usage.${label}`, takenKeys);
      }
    });

    identifierRows.push({
      identifier: id.identifier ?? '',
      utilityType: id.utilityType ?? '',
      siteAddress: id.siteAddress ?? '',
      siteCity: id.siteCity ?? '',
      siteState: id.siteState ?? '',
      siteCountry: id.siteCountry ?? '',
      sitePostCode: id.sitePostCode ?? '',
      periodFrom: id.periodFrom ?? '',
      periodTo: id.periodTo ?? '',
      serviceTotal: id.serviceTotal ?? null,
    });

    const charges: any[] = Array.isArray(id.parserchargesdata) ? id.parserchargesdata : [];
    charges.forEach((c, cIdx) => {
      // Meter fields to Meter category
      put(categories, 'Meter', 'Meter Number', c.meterNumber, `ident.${idx}.charges.${cIdx}.meterNumber`, takenKeys);
      put(categories, 'Meter', 'Multiplier', c.meterMultiplier, `ident.${idx}.charges.${cIdx}.meterMultiplier`, takenKeys);
      put(categories, 'Meter', 'Previous Reading', c.previousReading, `ident.${idx}.charges.${cIdx}.prev`, takenKeys);
      put(categories, 'Meter', 'Current Reading', c.currentReading, `ident.${idx}.charges.${cIdx}.curr`, takenKeys);
      put(categories, 'Meter', 'Quantity', c.quantity, `ident.${idx}.charges.${cIdx}.qty`, takenKeys);
      put(categories, 'Meter', 'Unit', c.quantityUOM, `ident.${idx}.charges.${cIdx}.uom`, takenKeys);

      meterRows.push({
        identifier: id.identifier ?? '',
        meterNumber: c.meterNumber ?? '',
        multiplier: c.meterMultiplier ?? '',
        previousReading: c.previousReading ?? '',
        currentReading: c.currentReading ?? '',
        quantity: c.quantity ?? '',
        unit: c.quantityUOM ?? '',
      });

      // --- Line Items table shape (normalized) ---
      const li: LineItemRow = {
        identifier:  id.identifier ?? '',
        meterNumber: c.meterNumber ?? '',
        description: c.lineItemDescription ?? c.lineItem ?? '',
        quantity:    c.quantity ?? '',
        unit:        c.quantityUOM ?? '',
        rate:        c.rate ?? '',
        total:       c.total ?? '',
      };
      lineItemRows.push(li);

      // Also add summarized fields into the Line Items category (for card view)
      put(categories, 'LineItems', 'Description', li.description, `ident.${idx}.charges.${cIdx}.desc`, takenKeys);
      if (li.quantity !== '') put(categories, 'LineItems', 'Quantity', li.quantity, `ident.${idx}.charges.${cIdx}.qty`, takenKeys);
      if (li.unit !== '')     put(categories, 'LineItems', 'Unit',     li.unit,     `ident.${idx}.charges.${cIdx}.uom`, takenKeys);
      if (li.rate !== '')     put(categories, 'LineItems', 'Rate',     li.rate,     `ident.${idx}.charges.${cIdx}.rate`, takenKeys);
      if (li.total !== '')    put(categories, 'LineItems', 'Line Total', li.total,  `ident.${idx}.charges.${cIdx}.total`, takenKeys);
    });
  });

  // 5) Optionally route TECHNICAL and leftovers (scalars only)
  const tech = resultsObj?.confidence_summary;
  if (tech && typeof tech === 'object') {
    Object.entries(tech).forEach(([k,v]) => isRenderable(v) && put(categories, 'Technical', `confidence_${k}`, v, `tech.${k}`, takenKeys));
  }

  // 6) Return the exact shape the UI reads:
  console.debug('[XFORM] usage.count', (categories[CAT.Usage] ?? []).length);
  console.debug('[XFORM] lineItems.rows', (lineItemRows ?? []).length);
  
  return { 
    categories, 
    identifiers: identifierRows, 
    meters: meterRows, 
    lineItems: lineItemRows,
    totalFields: Object.values(categories).reduce((sum, cat) => sum + cat.length, 0)
  };
}